import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  CreditCard, Home, History, User, Settings, Bell,
  CheckCircle, Shield, Smartphone, Globe, Wallet,
  Mail, Phone, Calendar, MapPin, Edit, Lock
} from "lucide-react";

export default function PlatiPomiruProfile() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    const dataParam = searchParams.get('data');
    let parsed = null;
    
    if (dataParam) {
      try {
        parsed = JSON.parse(decodeURIComponent(dataParam));
      } catch (e) {}
    }
    
    if (!parsed) {
      try {
        const stored = localStorage.getItem('platipomiru_preview_data');
        if (stored) parsed = JSON.parse(stored);
      } catch (e) {}
    }
    
    if (parsed) setData(parsed);
  }, [searchParams]);

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-slate-500 text-lg">Загрузка...</div>
      </div>
    );
  }

  const menuItems = [
    { icon: Home, label: 'Главная' },
    { icon: CreditCard, label: 'Моя карта' },
    { icon: History, label: 'История' },
    { icon: Wallet, label: 'Пополнить' },
    { icon: User, label: 'Профиль', active: true },
    { icon: Settings, label: 'Настройки' },
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fb] template-preview-scale" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-violet-600 rounded-xl flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-slate-800 text-lg">Плати по миру</span>
                <p className="text-xs text-slate-500">Virtual Cards</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button className="relative p-2 text-slate-400 hover:text-slate-600">
                <Bell className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-violet-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {data.firstName?.[0]}{data.lastName?.[0]}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-slate-800">{data.fullName}</p>
                  <p className="text-xs text-slate-500">ID: {data.userId}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <nav className="space-y-1">
                {menuItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={index}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                        item.active 
                          ? 'bg-gradient-to-r from-blue-500 to-violet-600 text-white shadow-md' 
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3 space-y-6">
            {/* Profile Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="h-32 bg-gradient-to-r from-blue-500 via-violet-500 to-purple-600"></div>
              <div className="px-6 pb-6">
                <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-12">
                  <div className="w-24 h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center border-4 border-white">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-violet-600 rounded-xl flex items-center justify-center">
                      <span className="text-white text-2xl font-bold">
                        {data.firstName?.[0]}{data.lastName?.[0]}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 md:mb-2">
                    <h1 className="text-2xl font-bold text-slate-800">{data.fullName}</h1>
                    <p className="text-slate-500">ID: {data.userId}</p>
                  </div>
                  <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 text-sm font-medium flex items-center gap-2">
                    <Edit className="w-4 h-4" />
                    Редактировать
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Info */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Личные данные</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">Имя</p>
                      <p className="font-medium text-slate-800">{data.firstName}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">Фамилия</p>
                      <p className="font-medium text-slate-800">{data.lastName}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">Телефон</p>
                      <p className="font-medium text-slate-800">{data.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium text-slate-800">{data.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-pink-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">Дата регистрации</p>
                      <p className="font-medium text-slate-800">{data.registrationDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security & Features */}
              <div className="space-y-6">
                {/* Verification Status */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <h2 className="text-lg font-bold text-slate-800 mb-4">Верификация</h2>
                  
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-800">Аккаунт верифицирован</p>
                      <p className="text-sm text-green-600">Все функции доступны</p>
                    </div>
                  </div>
                </div>

                {/* Connected Services */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <h2 className="text-lg font-bold text-slate-800 mb-4">Подключённые сервисы</h2>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                          <span className="text-white text-xs font-bold">Pay</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Apple Pay</p>
                          <p className="text-xs text-slate-500">Бесконтактные платежи</p>
                        </div>
                      </div>
                      {data.applePay ? (
                        <div className="flex items-center gap-1.5 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Подключено</span>
                        </div>
                      ) : (
                        <button className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-lg">Подключить</button>
                      )}
                    </div>

                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-lg flex items-center justify-center">
                          <span className="text-white text-xs font-bold">G</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Google Pay</p>
                          <p className="text-xs text-slate-500">Бесконтактные платежи</p>
                        </div>
                      </div>
                      {data.googlePay ? (
                        <div className="flex items-center gap-1.5 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Подключено</span>
                        </div>
                      ) : (
                        <button className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-lg">Подключить</button>
                      )}
                    </div>

                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                          <Shield className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">3D Secure</p>
                          <p className="text-xs text-slate-500">Защита платежей</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Активно</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Security Settings */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <h2 className="text-lg font-bold text-slate-800 mb-4">Безопасность</h2>
                  
                  <div className="space-y-3">
                    <button className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                      <div className="flex items-center gap-3">
                        <Lock className="w-5 h-5 text-slate-600" />
                        <span className="font-medium text-slate-800">Сменить пароль</span>
                      </div>
                      <span className="text-slate-400">→</span>
                    </button>

                    <button className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-5 h-5 text-slate-600" />
                        <span className="font-medium text-slate-800">Двухфакторная аутентификация</span>
                      </div>
                      <span className="text-green-600 text-sm font-medium">Включена</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
